import { LeadSource } from '@up2stay/up2promo-api-client';
import {
  Button,
  Checkbox,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
  cn,
} from '@up2stay/up2promo-ui';
import {
  cloneElement,
  isValidElement,
  useId,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';

import {
  clearLeadsOverflowFilters,
  countActiveLeadsOverflowFilters,
  type LeadsCreatedWithinPreset,
  type LeadsListFilterCriteria,
} from '@/core/components/leads/lead-filter-normalize';

export type LeadsListFilterValues = LeadsListFilterCriteria;

const FILTERABLE_SOURCES: LeadSource[] = [
  LeadSource.PUBLIC_FORM,
  LeadSource.MANUAL,
  LeadSource.BUSINESS_CARD,
];

const CREATED_WITHIN_ORDER: LeadsCreatedWithinPreset[] = [
  'all',
  'last_day',
  'last_week',
  'last_month',
];

/** Toolbar-style overflow: scroll without visible scrollbar (popover). */
const POPOVER_SCROLL_HIDE =
  '[scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden';

function toggleInList<T>(list: T[], item: T): T[] {
  return list.includes(item) ? list.filter((x) => x !== item) : [...list, item];
}

export type LeadsListFiltersLabels = {
  filter: string;
  filtersTitle: string;
  clearAll: string;
  done: string;
  sectionSource: string;
  sectionTags: string;
  noTagsToFilter: string;
  sectionPeriod: string;
  periodAll: string;
  periodLastDay: string;
  periodLastWeek: string;
  periodLastMonth: string;
  sourceLabels: Record<string, string>;
};

type LeadsListFiltersProps = {
  value: LeadsListFilterValues;
  onChange: (next: LeadsListFilterValues) => void;
  tagOptions: string[];
  labels: LeadsListFiltersLabels;
  /** Optional custom open control (CountPill / text link). */
  trigger?: ReactNode;
};

/**
 * Desktop “Mehr” overflow popover: Tags / Source / Period.
 * Event, Assignee, and Status live in the main chip row.
 */
export function LeadsListFilters({
  value,
  onChange,
  tagOptions,
  labels,
  trigger,
}: LeadsListFiltersProps) {
  const [open, setOpen] = useState(false);

  const activeCount = useMemo(
    () => countActiveLeadsOverflowFilters(value),
    [value],
  );

  const openControl =
    trigger && isValidElement(trigger) ? (
      cloneElement(
        trigger as ReactElement<{
          onClick?: () => void;
          'aria-expanded'?: boolean;
        }>,
        {
          'aria-expanded': open,
        },
      )
    ) : (
      <Button
        type="button"
        variant="outline"
        size="sm"
        aria-label={labels.filter}
        className={cn(
          'h-[30px] rounded-full px-3 text-[12.5px]',
          activeCount > 0 &&
            'border-[color:var(--ghost-accent-border)] bg-[color:var(--ghost-accent-bg)] text-[color:var(--ghost-accent-fg)]',
        )}
      >
        {labels.filter}
        {activeCount > 0 ? (
          <span className="ml-1 font-mono text-[11px]">{activeCount}</span>
        ) : null}
      </Button>
    );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{openControl}</PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={8}
        className="w-[min(calc(100vw-2rem),20rem)] p-4"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <OverflowFilterPanel
          value={value}
          onChange={onChange}
          tagOptions={tagOptions}
          labels={labels}
          onRequestClose={() => setOpen(false)}
        />
      </PopoverContent>
    </Popover>
  );
}

function OverflowFilterPanel({
  value,
  onChange,
  tagOptions,
  labels,
  onRequestClose,
}: {
  value: LeadsListFilterValues;
  onChange: (next: LeadsListFilterValues) => void;
  tagOptions: string[];
  labels: LeadsListFiltersLabels;
  onRequestClose: () => void;
}) {
  const periodSelectId = useId();
  const activeCount = countActiveLeadsOverflowFilters(value);

  const periodLabels: Record<LeadsCreatedWithinPreset, string> = {
    all: labels.periodAll,
    last_day: labels.periodLastDay,
    last_week: labels.periodLastWeek,
    last_month: labels.periodLastMonth,
  };

  return (
    <div className="flex max-h-[min(70vh,28rem)] flex-col">
      <div
        className={cn(
          'min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-contain px-1 py-1',
          POPOVER_SCROLL_HIDE,
        )}
      >
        <section className="space-y-1">
          <h3 className="px-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            {labels.sectionSource}
          </h3>
          <div className="space-y-0.5">
            {FILTERABLE_SOURCES.map((source) => {
              const checked = value.sources.includes(source);
              const label = labels.sourceLabels[source] ?? source;
              return (
                <FilterCheckRow
                  key={source}
                  checked={checked}
                  label={label}
                  onToggle={() =>
                    onChange({
                      ...value,
                      sources: toggleInList(value.sources, source),
                    })
                  }
                />
              );
            })}
          </div>
        </section>

        <Separator />

        <section className="space-y-2">
          <Label
            htmlFor={periodSelectId}
            className="block px-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground"
          >
            {labels.sectionPeriod}
          </Label>
          <div className="px-2">
            <Select
              value={value.createdWithin}
              onValueChange={(v) =>
                onChange({
                  ...value,
                  createdWithin: v as LeadsCreatedWithinPreset,
                })
              }
            >
              <SelectTrigger id={periodSelectId} className="h-9 w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent position="popper" sideOffset={6}>
                {CREATED_WITHIN_ORDER.map((preset) => (
                  <SelectItem key={preset} value={preset}>
                    {periodLabels[preset]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </section>

        <Separator />

        <section className="space-y-1">
          <h3 className="px-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            {labels.sectionTags}
          </h3>
          {tagOptions.length === 0 ? (
            <p className="px-2 py-2 text-sm text-muted-foreground">
              {labels.noTagsToFilter}
            </p>
          ) : (
            <div className="space-y-0.5">
              {tagOptions.map((tag) => {
                const checked = value.tags.includes(tag);
                return (
                  <FilterCheckRow
                    key={tag}
                    checked={checked}
                    label={tag}
                    onToggle={() =>
                      onChange({
                        ...value,
                        tags: toggleInList(value.tags, tag),
                      })
                    }
                  />
                );
              })}
            </div>
          )}
        </section>
      </div>

      <div className="mt-3 flex shrink-0 items-center justify-between gap-2 border-t border-border/60 pt-3">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 px-2 text-muted-foreground hover:text-foreground"
          disabled={activeCount === 0}
          onClick={() => onChange(clearLeadsOverflowFilters(value))}
        >
          {labels.clearAll}
        </Button>
        <Button
          type="button"
          size="sm"
          className="h-8"
          onClick={onRequestClose}
        >
          {labels.done}
        </Button>
      </div>
    </div>
  );
}

function FilterCheckRow({
  checked,
  label,
  onToggle,
}: {
  checked: boolean;
  label: string;
  onToggle: () => void;
}) {
  const checkboxId = useId();
  return (
    <div className="flex items-center gap-3 rounded-lg px-2 py-2">
      <Checkbox
        id={checkboxId}
        checked={checked}
        onCheckedChange={() => onToggle()}
        className="border-border/80"
      />
      <Label
        htmlFor={checkboxId}
        className="min-w-0 flex-1 cursor-pointer text-left text-sm font-normal leading-snug text-foreground"
      >
        {label}
      </Label>
    </div>
  );
}
